import { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { loadRuntimeConfig } from './lib/config';
import { configureAmplify } from './lib/auth';
import { useAuth } from './hooks/useAuth';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { LoginModal } from './components/auth/LoginModal';
import { DisplayNameModal } from './components/auth/DisplayNameModal';
import { HomePage } from './pages/HomePage';
import { AboutPage } from './pages/AboutPage';
import { RulesPage } from './pages/RulesPage';
import { SchedulePage } from './pages/SchedulePage';
import { ReservePage } from './pages/ReservePage';
import { ScoreboardPage } from './pages/ScoreboardPage';
import { ArchivePage } from './pages/ArchivePage';
import { NewsPage } from './pages/NewsPage';
import { NewsDetailPage } from './pages/NewsDetailPage';
import { LinksPage } from './pages/LinksPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { ProfilePage } from './pages/ProfilePage';
import { OfficialRulesPage } from './pages/OfficialRulesPage';
import { AdminCreateNewsPage } from './pages/AdminCreateNewsPage';
import { AdminAddGameResultPage } from './pages/AdminAddGameResultPage';
import { EditGameResultPage } from './pages/EditGameResultPage';
import { AdminPlayersPage } from './pages/AdminPlayersPage';
import { AdminFinancePage } from './pages/AdminFinancePage';
import { AdminFinanceDayPage } from './pages/AdminFinanceDayPage';
import { AdminGameLevelsPage } from './pages/AdminGameLevelsPage';
import { AdminAnalyticsPage } from './pages/AdminAnalyticsPage';
import { useAnalytics } from './hooks/useAnalytics';
import { GamePage } from './pages/GamePage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function AppShell() {
  const [loginOpen, setLoginOpen] = useState(false);
  useAuth();
  useAnalytics();

  return (
    <div className="min-h-screen flex flex-col">
      <Header onLoginClick={() => setLoginOpen(true)} />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
      <DisplayNameModal />
      <div className="flex-1">
        <Suspense fallback={
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-mafia-accent border-t-transparent rounded-full animate-spin" />
          </div>
        }>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/rules" element={<RulesPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/reserve" element={<ReservePage onLoginRequired={() => setLoginOpen(true)} />} />
            <Route path="/scoreboard" element={<ScoreboardPage />} />
            <Route path="/archive" element={<ArchivePage />} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:id" element={<NewsDetailPage />} />
            <Route path="/links" element={<LinksPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/rules/official" element={<OfficialRulesPage />} />
            <Route path="/admin/news/new" element={<AdminCreateNewsPage />} />
            <Route path="/admin/scoreboard/new" element={<AdminAddGameResultPage />} />
            <Route path="/admin/scoreboard/edit/:seasonId/:gameId" element={<EditGameResultPage />} />
            <Route path="/admin/scoreboard/players" element={<AdminPlayersPage />} />
            <Route path="/admin/game" element={<AdminGameLevelsPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="/admin/finance" element={<AdminFinancePage />} />
            <Route path="/admin/finance/days/:date" element={<AdminFinanceDayPage />} />
            <Route path="/game" element={<GamePage onLoginRequired={() => setLoginOpen(true)} />} />
            <Route path="/game/:levelId" element={<GamePage onLoginRequired={() => setLoginOpen(true)} />} />
          </Routes>
        </Suspense>
      </div>
      <Footer />
    </div>
  );
}

export default function App() {
  const [configReady, setConfigReady] = useState(false);

  useEffect(() => {
    loadRuntimeConfig()
      .then((config) => {
        configureAmplify(config);
        window.__STRIPE_PK__ = config.stripePk;
        setConfigReady(true);
      })
      .catch(() => {
        // Dev fallback: skip runtime config
        setConfigReady(true);
      });
  }, []);

  if (!configReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mafia-dark">
        <div className="w-8 h-8 border-2 border-mafia-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}
